import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router';

import { deckKeys, reviewQueueKey } from '@/entities/deck';
import { listDecks } from '@/shared/api/decks';
import { getReviewSummary } from '@/shared/api/review';
import { currentTimeZone } from '@/shared/lib/timeZone';
import { ErrorMessage, LinkButton } from '@/shared/ui';

import styles from './LearnPage.module.css';

function Counts({ due, fresh }: { due: number; fresh: number }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <span className={styles.counts}>
      {due + fresh === 0 ? t('learn.nothing') : t('learn.counts', { due, fresh })}
    </span>
  );
}

/**
 * What to study: every deck at once or one of them, each with what it has
 * for today, so that the choice is made before the session starts.
 */
export function LearnPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const legacyDeck = params.get('deck');
  const summary = useQuery({
    queryKey: [...reviewQueueKey, 'summary-by-deck'],
    queryFn: ({ signal }) => getReviewSummary({ timeZone: currentTimeZone(), signal }),
    staleTime: 0,
  });
  const decks = useQuery({ queryKey: deckKeys.all, queryFn: ({ signal }) => listDecks(signal) });

  if (legacyDeck) {
    return <Navigate to={`/review/${legacyDeck}`} replace />;
  }

  const error = summary.error ?? decks.error;
  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!summary.data || !decks.data) {
    return <p className={styles.muted}>{t('common.loading')}</p>;
  }

  const counts = new Map(summary.data.decks.map((entry) => [entry.deck_id, entry]));
  const allWork = summary.data.due_count + summary.data.new_count;
  const noCards = decks.data.every((deck) => deck.card_count === 0);

  return (
    <div className={styles.page}>
      <h1>{t('learn.title')}</h1>

      {decks.data.length === 0 ? (
        <section className={styles.all}>
          <p>{t('learn.noDecks')}</p>
          <LinkButton to="/decks/new" variant="primary">
            {t('learn.createDeck')}
          </LinkButton>
        </section>
      ) : noCards ? (
        <section className={styles.all}>
          <p>{t('learn.noCards')}</p>
          <LinkButton to="/decks" variant="primary">
            {t('learn.toDecks')}
          </LinkButton>
        </section>
      ) : (
        <>
          <section className={styles.all} aria-labelledby="learn-all">
            <div>
              <h2 id="learn-all">{t('learn.all')}</h2>
              <Counts due={summary.data.due_count} fresh={summary.data.new_count} />
            </div>
            {allWork > 0 && (
              <LinkButton to="/review/all" variant="primary" className={styles.cta}>
                {t('learn.start')}
              </LinkButton>
            )}
          </section>

          <section aria-labelledby="learn-decks">
            <h2 id="learn-decks" className={styles.subtitle}>
              {t('learn.byDeck')}
            </h2>
            <ul className={styles.list}>
              {decks.data.map((deck) => {
                const entry = counts.get(deck.id);
                const due = entry?.due_count ?? 0;
                const fresh = entry?.new_count ?? 0;

                return (
                  <li key={deck.id} className={styles.row}>
                    <div className={styles.rowText}>
                      <span className={styles.name}>{deck.title}</span>
                      {deck.card_count === 0 ? (
                        <span className={styles.counts}>{t('learn.emptyDeck')}</span>
                      ) : (
                        <Counts due={due} fresh={fresh} />
                      )}
                    </div>
                    {due + fresh > 0 && (
                      <LinkButton
                        to={`/review/${deck.id}`}
                        aria-label={t('learn.startDeck', { title: deck.title })}
                      >
                        {t('learn.start')}
                      </LinkButton>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
